<div class="fcomp-shell">

  <div class="fcomp-header">
    <div>
      <h2 class="fcomp-header__title">{{ 'FLEXI_COMPLIANCE.TITLE' | translate }}</h2>
      <p class="fcomp-header__meta">
        <span>{{ viewed.catalogVersionName }}</span>
        <span class="fcomp-dot" *ngIf="viewed.completedOn">·</span>
        <span *ngIf="viewed.completedOn">
          {{ 'FLEXI_COMPLIANCE.COMPLETED_ON' | translate }} {{ viewed.completedOn | date:'dd-MM-yyyy' }}
        </span>
      </p>
    </div>
    <div class="fcomp-header__status" [ngClass]="{
      'is-completed': displayStatus === Status.Completed,
      'is-archived': displayStatus === Status.Archived,
      'is-superseded': displayStatus === Status.Superseded
    }">
      <i class="pi" [ngClass]="{
        'pi-check-circle': displayStatus === Status.Completed,
        'pi-folder': displayStatus === Status.Archived,
        'pi-history': displayStatus === Status.Superseded
      }"></i>
      <span *ngIf="displayStatus === Status.Archived">{{ 'FLEXI_COMPLIANCE.STATUS.ARCHIVED' | translate }}</span>
      <span *ngIf="displayStatus === Status.Superseded">{{ 'FLEXI_COMPLIANCE.STATUS.SUPERSEDED' | translate }}</span>
      <span *ngIf="displayStatus !== Status.Archived && displayStatus !== Status.Superseded">{{ 'FLEXI_COMPLIANCE.STATUS.COMPLETED' | translate }}</span>
    </div>
  </div>

  <!-- Inline error banner from the download attempt -->
  <div class="fcomp-banner fcomp-banner--error" *ngIf="errorMessage">
    <i class="pi pi-exclamation-triangle"></i>
    <div>{{ errorMessage }}</div>
    <button type="button" class="fcomp-banner__action" (click)="errorMessage = null">
      {{ 'FLEXI_COMPLIANCE.DISMISS' | translate }}
    </button>
  </div>

  <!-- Download toolbar — only when there's a completed assessment to download -->
  <div class="fcomp-toolbar" *ngIf="viewed?.sections?.length">
    <div class="fcomp-toolbar__spacer"></div>
    <button type="button" class="fcomp-btn fcomp-btn--primary"
            (click)="downloadReportPdf()"
            [disabled]="downloading">
      <i class="pi" [ngClass]="downloading ? 'pi-spin pi-spinner' : 'pi-download'"></i>
      {{ 'FLEXI_COMPLIANCE.ACTIONS.DOWNLOAD_PDF' | translate }}
    </button>
  </div>

  <!-- "Previous reviews" strip. Chips are selectable: picking one shows that
       review read-only right here, and picking the current one comes back —
       no trip through the history grid. businessId stays bound to the real
       aggregate so previewing does not re-root the history on a snapshot. -->
  <flexi-compliance-history-strip *ngIf="showHistory && aggregate?.businessId"
                                  [businessId]="aggregate.businessId"
                                  [selectedId]="peeked?.id ?? null"
                                  (reviewSelected)="onHistorySelected($event)">
  </flexi-compliance-history-strip>

  <!-- Preview banner: says what is on screen and offers the way back. -->
  <div class="fcomp-banner fcomp-banner--info fcomp-peek-banner" *ngIf="peeked">
    <i class="pi pi-history"></i>
    <div class="fcomp-peek-banner__text">
      {{ 'FLEXI_COMPLIANCE.HISTORY.VIEWING_PAST' | translate }}
      <strong *ngIf="peekedOn">{{ peekedOn | date:'mediumDate' }}</strong>
      <small>{{ 'FLEXI_COMPLIANCE.HISTORY.READ_ONLY' | translate }}</small>
    </div>
    <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
            (click)="backToCurrent()">
      <i class="pi pi-arrow-left"></i>
      {{ 'FLEXI_COMPLIANCE.HISTORY.BACK_TO_CURRENT' | translate }}
    </button>
  </div>

  <div class="fcomp-peek-loading" *ngIf="peekLoading">
    <i class="pi pi-spin pi-spinner"></i>
    {{ 'FLEXI_COMPLIANCE.LOADING' | translate }}
  </div>

  <div class="fcomp-score" [ngClass]="smileyClass()">
    <div class="fcomp-score__face">
      <img [src]="smileyImage()" alt="" class="fcomp-score__face-img"
           [@flexiEmojiEntry]="emojiEntryState" />
    </div>
    <div class="fcomp-score__body">
      <div class="fcomp-score__top">
        <strong>{{ score.percentage }}%</strong>
        <span class="fcomp-score__counts">
          {{ score.greenCount }} {{ 'FLEXI_COMPLIANCE.RATINGS.GOOD' | translate }} ·
          {{ score.yellowCount }} {{ 'FLEXI_COMPLIANCE.RATINGS.NEEDS_IMPROVEMENT' | translate }} ·
          {{ score.redCount }} {{ 'FLEXI_COMPLIANCE.RATINGS.MISSING' | translate }}
        </span>
      </div>
      <div class="fcomp-score__bar">
        <div class="fcomp-score__fill" [style.width]="score.percentage + '%'"></div>
      </div>
    </div>
  </div>

  <section class="fcomp-section" *ngFor="let s of viewed.sections; let si = index"
           [ngClass]="'tone-' + sectionTone(s)"
           [class.is-open]="isOpen(s.id)">
    <!-- Collapsed by default and expandable on click. A completed review has
         every section rated, so showing them all open buried the score under
         fifty-odd questions the reader had not asked for. -->
    <header class="fcomp-section__head"
            (click)="toggleSection(s.id)"
            (keyup.enter)="toggleSection(s.id)"
            role="button"
            tabindex="0"
            [attr.aria-expanded]="isOpen(s.id)">
      <div class="fcomp-section__head-left">
        <span class="fcomp-section__num">{{ si + 1 }}</span>
        <div class="fcomp-section__title-block">
          <h4>{{ s.title }}</h4>
          <small class="fcomp-section__progress-bar">
            <span class="fcomp-section__progress-fill" [style.width]="sectionProgressPercent(s) + '%'"></span>
          </small>
        </div>
      </div>
      <div class="fcomp-section__head-right">
        <span class="fcomp-section__pill" [ngClass]="'tone-' + sectionTone(s)"
              *ngIf="sectionTone(s) as tone">
          <i class="pi" [ngClass]="{
              'pi-circle': tone === 'pending',
              'pi-spinner': tone === 'in-progress',
              'pi-times-circle': tone === 'red',
              'pi-exclamation-circle': tone === 'yellow',
              'pi-check-circle': tone === 'green'
            }"></i>
          <span *ngIf="tone === 'pending'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.PENDING' | translate }}</span>
          <span *ngIf="tone === 'in-progress'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.IN_PROGRESS' | translate }}</span>
          <span *ngIf="tone === 'red'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.RED' | translate }}</span>
          <span *ngIf="tone === 'yellow'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.YELLOW' | translate }}</span>
          <span *ngIf="tone === 'green'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.GREEN' | translate }}</span>
        </span>
        <span class="fcomp-section__count">
          {{ sectionRatedCount(s) }} / {{ sectionTotalCount(s) }}
        </span>
        <i class="pi fcomp-section__chevron"
           [ngClass]="isOpen(s.id) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
      </div>
    </header>
    <div class="fcomp-section__body" *ngIf="isOpen(s.id)">
      <div class="fcomp-item" *ngFor="let item of visibleItems(s); let i = index">
        <div class="fcomp-item__head">
          <span class="fcomp-item__pos">{{ si + 1 }}.{{ i + 1 }}</span>
          <div class="fcomp-item__body">
            <p class="fcomp-item__question">{{ item.question }}</p>
            <small class="fcomp-item__ref" *ngIf="item.regulatoryReference">{{ item.regulatoryReference }}</small>
          </div>
          <span class="fcomp-pill" [ngClass]="ratingClass(item.rating)">
            {{ (ratingLabelKey(item.rating) | translate) || ('FLEXI_COMPLIANCE.RATINGS.UNRATED' | translate) }}
          </span>
        </div>
        <p class="fcomp-item__comment" *ngIf="item.comment">{{ item.comment }}</p>

        <!-- Action points: read-only listing -->
        <div class="fcomp-actions-list" *ngIf="item.actionPoints?.length">
          <div class="fcomp-action"
               *ngFor="let action of item.actionPoints"
               [class.is-done]="action.status === ApStatus.Completed">
            <i class="pi fcomp-action__icon"
               [ngClass]="action.status === ApStatus.Completed ? 'pi-check-circle' : 'pi-circle'"></i>
            <div class="fcomp-action__body">
              <p class="fcomp-action__title">{{ action.title }}</p>
              <p class="fcomp-action__desc" *ngIf="action.description">{{ action.description }}</p>
              <div class="fcomp-action__meta">
                <span class="fcomp-chip">
                  <i class="pi pi-user"></i>
                  {{ responsibleLabelKey(action.responsibleParty) | translate }}{{ responsibleNameSuffix(action) }}
                </span>
                <span class="fcomp-chip" *ngIf="action.dueDate">
                  <i class="pi pi-calendar"></i>
                  {{ 'FLEXI_COMPLIANCE.ACTION.DUE' | translate }}: {{ action.dueDate | date:'dd-MM-yyyy' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</div>
